import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Users, User } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import apiClient from '../config/axios';
import { entityTypeLabel, entityTypePath } from '../utils/entityTypes';
import previewText from '../utils/suggestionPreview';
import entityTypeIcon from '../utils/entityIcons';
import useSuggestList from '../hooks/useSuggestList';
import useOtherSystemMatches, { otherSystemHref } from '../hooks/useOtherSystemMatches';
import { useSystem } from '../context/SystemContext';
import { campaignHref, needsFullLoad } from '../utils/campaignLinks';

// Global omnisearch box (#139) — lives in the site header on every page.
//
// A compact variant of SearchAutocomplete that queries the cross-entity
// GET /api/search endpoint (results arrive grouped by entity type, contiguous
// per type) and navigates to `/<entity_type>?slug=<slug>` on selection, passing
// state.from per the list↔detail nav contract so the detail page's back link
// returns here. Self-contained input state (unlike SearchAutocomplete, which is
// controlled by its list page's filter state).
//
// Keys and dismissal come from the shared useSuggestList hook (issue 1243):
// Enter opens the highlighted result, or the top one when nothing is
// highlighted (the site has no full results page to send it to), and a
// search that finds nothing says "Nothing matches".
//
// Signed in (issue 1587), the answer also carries `mine`: the person's own
// campaigns and characters by name, in every system. They head the list as a
// group of their own ("Yours"); one of another system opens with a full load.
const PER_TYPE_LIMIT = 5;
const MINE = 'mine';

/** Where one of my own results opens. */
export function mineHref(m) {
  if (m.kind === 'campaign') return campaignHref(m);
  return `/characters?id=${encodeURIComponent(m.id)}${needsFullLoad(m) ? `&system=${encodeURIComponent(m.system)}` : ''}`;
}

// `idPrefix`, `inputRef`, `ariaLabel` and `large` let a second box share the
// page with the header's (the homepage's hero search, issue 1587) without
// clashing ids.
const OmniSearch = ({
  placeholder = 'Search everything…', autoFocus = false, onNavigate,
  idPrefix = 'omnisearch', inputRef = undefined, ariaLabel = 'Search all content', large = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [mine, setMine] = useState([]);
  // The query the current results answer: Enter acts only on an answer to
  // what is in the box, and an empty answer can name it.
  const [searched, setSearched] = useState('');
  // The query whose search failed (no answer, or an error): said in the
  // dropdown rather than the box sitting there as if it were still thinking.
  const [failed, setFailed] = useState('');
  const abortRef = useRef(null);

  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setResults([]);
      setMine([]);
      setSearched('');
      setFailed('');
      list.close();
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    apiClient.get(
      `/api/search?q=${encodeURIComponent(debouncedQuery.trim())}&limit=${PER_TYPE_LIMIT}`,
      { signal: controller.signal }
    )
      .then((res) => {
        setResults(res.data.results || []);
        setMine(Array.isArray(res.data.mine) ? res.data.mine : []);
        setSearched(debouncedQuery.trim());
        setFailed('');
        list.show();
      })
      .catch((err) => {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          console.error('Omnisearch error:', err);
          setResults([]);
          setMine([]);
          setSearched('');
          setFailed(debouncedQuery.trim());
          list.show();
        }
      });

    return () => controller.abort();
  }, [debouncedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Issue 1452: once this system has answered without an entry of exactly
  // that name, ask the other enabled systems and list their matches BELOW this
  // system's, labelled with their system. ("unluck" under Pathfinder finds the
  // item Unlucky Figurine, which is not what was asked for; the spell Unluck is
  // D&D 3.5e's.) Opening one is a full load naming the system (?system= is read
  // at load), which is also what switches to it.
  const { system: activeSystem, systems } = useSystem();
  const answeredNow = searched !== '' && searched === query.trim();
  const exactHere = results.some((r) => String(r.name || '').trim().toLowerCase() === searched.toLowerCase());
  const otherGroups = useOtherSystemMatches(
    searched,
    answeredNow && !exactHere,
    (slug, q, signal) => apiClient.get('/api/search', { params: { q, limit: PER_TYPE_LIMIT, system: slug }, signal })
      .then((res) => res.data?.results || []),
  );
  const others = otherGroups.flatMap(({ system: s, items }) => items.map((item) => ({ ...item, otherSystem: s })));
  const mineItems = mine.map((m) => ({ entity_type: MINE, name: m.name, slug: `${m.kind}:${m.id}`, mine: m }));
  const shown = [...mineItems, ...results, ...others];
  const firstOther = shown.findIndex((item) => item.otherSystem);
  const activeSystemName = ((systems || []).find((s) => s.slug === activeSystem) || {}).name || 'this system';

  const selectResult = (item) => {
    if (item.mine) {
      list.close();
      setResults([]);
      setMine([]);
      setSearched('');
      setQuery('');
      onNavigate?.(item);
      const href = mineHref(item.mine);
      if (needsFullLoad(item.mine)) window.location.assign(href);
      else navigate(href);
      return;
    }
    if (item.otherSystem) {
      list.close();
      onNavigate?.(item);
      window.location.assign(otherSystemHref(entityTypePath(item.entity_type).replace(/^\/+/, ''), item.slug, item.otherSystem.slug));
      return;
    }
    list.close();
    setResults([]);
    setSearched('');
    setQuery('');
    onNavigate?.(item);
    navigate(`${entityTypePath(item.entity_type)}?slug=${item.slug}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  const current = answeredNow;
  const failedNow = failed !== '' && failed === query.trim();
  const list = useSuggestList({
    count: current ? shown.length : 0,
    onPick: (i) => selectResult(shown[i]),
    // Enter alone never switches systems: only this system's top result.
    pickTopOnEnter: mineItems.length + results.length > 0,
  });
  const isOpen = list.open;
  const activeIndex = list.activeIndex;
  const setActiveIndex = list.setActiveIndex;

  return (
    <div className="relative" ref={list.wrapperRef}>
      <Search className="search-icon" aria-hidden="true" />
      <input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={list.onKeyDown}
        onFocus={() => { if (current || failedNow) list.show(); }}
        ref={inputRef}
        className={large ? 'search-input home-search-input' : 'search-input'}
        style={large ? undefined : { fontSize: '0.95rem', paddingTop: '0.35rem', paddingBottom: '0.35rem' }}
        role="combobox"
        aria-label={ariaLabel}
        aria-controls={`${idPrefix}-listbox`}
        aria-expanded={isOpen && shown.length > 0}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? `${idPrefix}-result-${activeIndex}` : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {isOpen && shown.length > 0 && (
        <ul
          id={`${idPrefix}-listbox`}
          className="autocomplete-dropdown"
          role="listbox"
          aria-label="Search results"
        >
          {shown.map((item, index) => {
            const prev = shown[index - 1];
            const newGroup = index === 0 || prev.entity_type !== item.entity_type || prev.otherSystem !== item.otherSystem;
            // Rich rows (issue 190): a type icon plus the derived one-line
            // preview under the name.
            const Icon = item.mine ? (item.mine.kind === 'campaign' ? Users : User) : entityTypeIcon(item.entity_type);
            const preview = item.mine
              ? [item.mine.kind === 'campaign' ? 'Campaign' : 'Character', item.mine.system_name].filter(Boolean).join(' · ')
              : previewText(item.entity_type, item.preview);
            return (
              <React.Fragment key={`${item.otherSystem ? item.otherSystem.slug : ''}:${item.entity_type}:${item.slug}`}>
                {index === firstOther && results.length === 0 && (
                  <li role="presentation" className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                    Nothing in {activeSystemName} matches “{searched}”. In other systems:
                  </li>
                )}
                {newGroup && (
                  <li
                    className="omnisearch-group px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-muted)' }}
                    aria-hidden="true"
                  >
                    {item.mine ? 'Yours' : entityTypeLabel(item.entity_type)}{item.otherSystem ? ` · ${item.otherSystem.name}` : ''}
                  </li>
                )}
                <li
                  id={`${idPrefix}-result-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className="px-3 py-2 text-sm cursor-pointer transition-colors duration-100"
                  style={
                    index === activeIndex
                      ? { background: 'var(--gold-dim)', color: 'var(--gold)' }
                      : { color: 'var(--text-primary)' }
                  }
                  onMouseDown={(e) => { e.preventDefault(); selectResult(item); }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block truncate">{item.name}</span>
                      {preview && (
                        <span className="block truncate text-xs" style={{ color: 'var(--text-muted)' }}>
                          {preview}
                        </span>
                      )}
                    </span>
                  </span>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
      {isOpen && current && shown.length === 0 && (
        <p className="autocomplete-dropdown px-3 py-2 text-sm" role="status" style={{ color: 'var(--text-muted)' }}>
          Nothing matches “{searched}”
        </p>
      )}
      {isOpen && failedNow && (
        <p className="autocomplete-dropdown px-3 py-2 text-sm" role="alert" style={{ color: 'var(--text-muted)' }}>
          Search could not be reached just now. Try again in a moment.
        </p>
      )}
    </div>
  );
};

export default OmniSearch;
